import { useCallback, useRef, useState } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { PAGE_PATHS } from "@/config/urls";
import { useHumhubSessionLogin } from "@/hooks/useHumhubSessionLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { getLatestOidcToken } from "@/lib/authSession";
import { AUTH_FLOW_TOKEN_EXPIRATION_BUFFER_MS } from "@/lib/authSessionConfig";
import type { OidcRefreshFailureReason } from "@/lib/authSessionErrors";
import {
  buildAuthSessionSnapshot,
  getOidcTokenCandidates,
} from "@/lib/authSessionTiming";
import { navigateAfterDraftFlush } from "@/lib/safeAppNavigation";
import { saveOriginalUrl } from "@/lib/TokenManager";

export interface AuthRecoveryResult {
  status: "recovered" | "reauth_required" | "retry_later";
  reason: string;
}

export interface ExecuteAuthFlowOptions {
  allowRedirect?: boolean;
}

const isTransientRefreshFailure = (
  status: OidcRefreshFailureReason,
): status is "network_error" | "rate_limited" | "http_error" =>
  status === "network_error" ||
  status === "rate_limited" ||
  status === "http_error";

const sanitizeOriginalUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;
    const oidcCallbackParams = ["code", "state", "session_state", "iss"];

    if (oidcCallbackParams.some((key) => params.has(key))) {
      oidcCallbackParams.forEach((key) => {
        params.delete(key);
      });
      parsedUrl.search = params.toString();
    }

    return parsedUrl.toString();
  } catch (_error) {
    return url;
  }
};

export const useAuthFlow = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const auth = useAuth();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const { loginToHumhubSession } = useHumhubSessionLogin();
  const authPromiseRef = useRef<Promise<AuthRecoveryResult> | null>(null);

  const isTokenExpired = useCallback((expiresAt?: number) => {
    if (!expiresAt) {
      return false;
    }

    return Date.now() >= expiresAt - AUTH_FLOW_TOKEN_EXPIRATION_BUFFER_MS;
  }, []);

  const tryHumhubLogin = useCallback(
    async (
      tokenInfo: { token: string; expiresAt?: number } | null | undefined,
    ) => {
      if (!tokenInfo?.token || isTokenExpired(tokenInfo.expiresAt)) {
        return false;
      }

      try {
        const result = await loginToHumhubSession(
          tokenInfo.token,
          tokenInfo.expiresAt,
        );
        if (result?.auth_token) {
          return true;
        }
      } catch (_error) {
        // ログ出力はログインフック側で行う
      }

      return false;
    },
    [isTokenExpired, loginToHumhubSession],
  );

  /**
   * OIDC認証開始（コールバックURL検出対応）
   */
  const startOidcAuth = useCallback(async (auth: AuthContextProps) => {
    // 現在のURLが既にコールバックURLの場合は保存しない
    const currentUrl = window.location.href;
    const isCallbackUrl = window.location.pathname === PAGE_PATHS.CALLBACK;
    if (!isCallbackUrl) {
      saveOriginalUrl(sanitizeOriginalUrl(currentUrl));
    }

    // OIDC認証リダイレクトの前に、編集中の下書きを退避する
    await navigateAfterDraftFlush({
      reason: "auth_reauthentication",
      navigate: () => auth.signinRedirect(),
    });
  }, []);

  /**
   * 統合認証フロー実行
   */
  const executeAuthFlow = useCallback(
    async ({
      allowRedirect = true,
    }: ExecuteAuthFlowOptions = {}): Promise<AuthRecoveryResult> => {
      if (authPromiseRef.current) {
        return authPromiseRef.current;
      }

      let startedRedirect = false;
      const authPromise = (async (): Promise<AuthRecoveryResult> => {
        setIsAuthenticating(true);

        try {
          const tokenCandidates = getOidcTokenCandidates(
            buildAuthSessionSnapshot(auth),
            getLatestOidcToken(),
          );

          for (const candidate of tokenCandidates) {
            if (await tryHumhubLogin(candidate)) {
              return { status: "recovered", reason: "existing_token" };
            }
          }

          const refreshResult = await refreshAccessToken();
          if (refreshResult.status === "success") {
            if (await tryHumhubLogin(refreshResult.tokenInfo)) {
              return { status: "recovered", reason: "refresh_success" };
            }

            return {
              status: "retry_later",
              reason: "humhub_session_login_failed",
            };
          }

          if (isTransientRefreshFailure(refreshResult.status)) {
            return { status: "retry_later", reason: refreshResult.status };
          }

          if (refreshResult.status === "invalid_refresh_token") {
            if (allowRedirect) {
              await startOidcAuth(auth);
              startedRedirect = true;
            }

            return {
              status: "reauth_required",
              reason: "invalid_refresh_token",
            };
          }

          return { status: "reauth_required", reason: "protocol_error" };
        } catch (error) {
          console.error("❌ Auth flow error:", error);
          return {
            status: "retry_later",
            reason:
              error instanceof Error ? error.message : "unknown_auth_error",
          };
        } finally {
          setIsAuthenticating(false);
          // Keep the terminal redirect promise so concurrent 401s cannot start another redirect.
          if (!startedRedirect) {
            authPromiseRef.current = null;
          }
        }
      })();

      authPromiseRef.current = authPromise;
      return authPromise;
    },
    [auth, refreshAccessToken, startOidcAuth, tryHumhubLogin],
  );

  return {
    executeAuthFlow,
    isAuthenticating,
  };
};
